import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { getRunActions, type AgentRunActions } from "./runActions";
import { findOrThrow, loadErrorMessage, swallowedFailure } from "shared/loadOrError";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import {
  AGENT_CAPABLE_PROVIDER_TYPES,
  AGENT_CHAT_TASK_KEY,
  AI_AGENT_AGENTS_PATH,
  AI_AGENT_CONVERSATIONS_PATH,
  AI_AGENT_MESSAGES_PATH,
  AI_AGENT_RUNS_PATH,
  AI_AGENT_TOOL_INVOCATIONS_PATH,
  OFFICE_MANAGER_AGENT_NAME,
  OFFICE_MANAGER_NAME,
  RUN_STREAM_EVENTS,
  type AgentConversation,
  type AgentDefinition,
  type AgentMessage,
  type AgentRun,
  type AgentToolInvocation,
  type ChatItem,
  type ChatToolCall,
  type RunStreamEvent,
} from "./agentChat.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** Max turns pulled for one conversation. The run loop itself reads 1000. */
const MESSAGE_PAGE_SIZE = 200;
/** Max conversations listed in the sidebar. */
const CONVERSATION_PAGE_SIZE = 50;
/** Longest generated conversation title (column is NVARCHAR(500)). */
const TITLE_MAX = 120;

/** The subset of a Feathers-ish event emitter this store subscribes to. */
interface EventfulService {
  on?: (event: string, handler: (payload: never) => void) => unknown;
  off?: (event: string, handler: (payload: never) => void) => unknown;
  removeListener?: (
    event: string,
    handler: (payload: never) => void,
  ) => unknown;
}

/** The one `llm-task-routes` row that decides which provider the agent dials. */
interface AgentChatRouteRow {
  taskKey: string;
  providerId: string | null;
  providerName: string | null;
  providerType: string | null;
  model: string | null;
}

/** Stable sort by `createdAt`, preserving server order for equal timestamps. */
const byCreatedAt = <T extends { createdAt: string }>(rows: T[]): T[] =>
  [...rows].sort((a, b) =>
    a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0,
  );

/** First line of the prompt, trimmed to something that fits a sidebar row. */
export const deriveConversationTitle = (text: string): string => {
  const firstLine = text.trim().split("\n")[0]?.trim() ?? "";
  const candidate = firstLine.length > 0 ? firstLine : text.trim();
  if (candidate.length <= TITLE_MAX) return candidate;
  return `${candidate.slice(0, TITLE_MAX - 1)}…`;
};

/**
 * Pull the human-readable reason out of a failed run.
 *
 * `runs.start` rejects with `GeneralError('run <id> failed during orchestration.',
 * <sanitized>)` where `<sanitized>` is `{message,name,code,status}` — and the
 * same JSON is persisted on `ai_agent_runs.error`. The OUTER message is useless
 * to a user ("failed during orchestration"); the inner one is the actual cause,
 * e.g. "ai-agent host: no Azure OpenAI target for the agent…". Prefer it.
 */
export const describeRunFailure = (
  error: unknown,
  run?: AgentRun | null,
): string => {
  const data = (error as { data?: { message?: unknown } } | null)?.data;
  if (data && typeof data.message === "string" && data.message.length > 0) {
    return data.message;
  }
  if (run?.error) {
    try {
      const parsed = JSON.parse(run.error) as { message?: unknown };
      if (typeof parsed.message === "string" && parsed.message.length > 0) {
        return parsed.message;
      }
    } catch {
      return run.error;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return "The agent run failed.";
};

/**
 * ViewStore for the AI → Rosie (HQ's office manager) chat.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. Every API entity lives in a ServiceStore
 * (serviceStores/aiAgent.stores.ts) — this store holds only view state
 * (selection, the draft, the transient stream buffer) and computes the
 * transcript over the ServiceStore caches.
 *
 * ── Streaming, not polling ────────────────────────────────────────────────
 * The API already pushes everything the transcript needs:
 *   • `run.stream.start|chunk|tool|end` — transient token/tool events the run
 *     loop emits, published by channels.ts to the personal channel of the user
 *     who created the run (they carry no userId; the host caches run ownership
 *     from the run's `created` event). These drive the live "typing" bubble.
 *   • `ai-agent-messages created` — the PERSISTED turns, which arrive over the
 *     normal Feathers realtime channel and land in the messages ServiceStore
 *     cache on their own. These are the source of truth; the stream buffer is
 *     discarded the moment the real assistant row shows up.
 * `runs.start` is itself an awaited socket call that resolves when the run
 * settles, so there is no state a poll could observe that these three signals do
 * not already deliver — and polling would burn the API's socket rate limit for
 * nothing.
 */
class AgentChatStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  /**
   * True when the last `load()` failed (agent, threads or the open thread).
   * `error` also carries archive/message failures; this flag is what stops the
   * sidebar saying "No conversations yet" about a list that never arrived.
   */
  loadFailed = false;

  /** Resolved once per session from the seeded agent row (`OFFICE_MANAGER_AGENT_NAME`). */
  agentId: string | null = null;
  agentModelId: string | null = null;
  agentToolNames: string[] = [];
  /** Set when the agent row itself is missing (seed never ran). */
  agentMissingReason: string | null = null;

  selectedConversationId: string | null = null;
  messagesLoading = false;

  /** Sidebar filter (header search box). Matches on the conversation title. */
  conversationQuery = "";

  draft = "";
  sending = false;
  sendError: string | null = null;

  /** The run currently being awaited, if any. */
  activeRunId: string | null = null;
  /** Live token buffer for `activeRunId`, replaced by the persisted message. */
  streamText = "";
  /** Tools the model asked for during the in-flight run, newest last. */
  streamToolNames: string[] = [];

  /** Best-effort readiness note about the `agent.chat` provider route. */
  configWarning: string | null = null;

  /**
   * Below `md` the thread list and the transcript are two SCREENS rather than
   * two panes; this is which one is showing. Ignored at `md`+, where both are
   * always visible.
   */
  conversationListOpen = false;

  private streamHandlers = new Map<string, (payload: never) => void>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ── ServiceStore accessors ───────────────────────────────────────────────

  private serviceStore(path: string): ServiceStoreType | null {
    const root = this.rootStore as unknown as Record<string, unknown>;
    const manager = root.serviceStoreManager as
      | { getStore?: (p: string) => unknown }
      | null
      | undefined;
    const camel = `${path.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase())}Store`;
    return (
      ((manager?.getStore?.(path) ?? root[camel]) as ServiceStoreType | null) ??
      null
    );
  }

  private get agentsService() {
    return this.serviceStore(AI_AGENT_AGENTS_PATH);
  }
  private get conversationsService() {
    return this.serviceStore(AI_AGENT_CONVERSATIONS_PATH);
  }
  private get messagesService() {
    return this.serviceStore(AI_AGENT_MESSAGES_PATH);
  }
  private get runsService() {
    return this.serviceStore(AI_AGENT_RUNS_PATH);
  }
  private get toolInvocationsService() {
    return this.serviceStore(AI_AGENT_TOOL_INVOCATIONS_PATH);
  }

  private get runActions(): AgentRunActions | null {
    return getRunActions(this.rootStore?.app as never);
  }

  private get currentUserId(): number | null {
    const auth = (
      this.rootStore as unknown as {
        authStore?: { currentUser?: { id?: number | string } | null };
      }
    ).authStore;
    const id = auth?.currentUser?.id;
    const numeric = typeof id === "string" ? Number(id) : id;
    return typeof numeric === "number" && Number.isFinite(numeric)
      ? numeric
      : null;
  }

  // ── Computed views over the ServiceStore caches ──────────────────────────

  /** The seeded agent row, when it has been loaded. */
  get agent(): AgentDefinition | null {
    const rows = (this.agentsService?.entities ??
      []) as unknown as AgentDefinition[];
    return rows.find((a) => a.name === OFFICE_MANAGER_AGENT_NAME) ?? null;
  }

  /**
   * This user's threads with the agent, newest activity first.
   *
   * Scoped to `userId` because a conversation is one person's private thread —
   * the sidebar must not list a colleague's. (The API does NOT enforce this: HQ
   * is single-tenant and every ai-agent row shares one `companyId`. It is an
   * honest UI scope, not a security boundary.)
   */
  get conversations(): AgentConversation[] {
    const rows = (this.conversationsService?.entities ??
      []) as unknown as AgentConversation[];
    const userId = this.currentUserId;
    const mine = rows.filter(
      (c) =>
        c.agentId === this.agentId &&
        c.status !== "archived" &&
        (userId === null || c.userId === userId),
    );
    return mine.sort((a, b) => {
      const at = a.lastMessageAt ?? a.createdAt;
      const bt = b.lastMessageAt ?? b.createdAt;
      return at < bt ? 1 : at > bt ? -1 : 0;
    });
  }

  /**
   * {@link conversations} narrowed by the header search box. Kept separate so a
   * filtered-out thread can still be the SELECTED one — typing in the search box
   * must not silently drop the transcript you are reading.
   */
  get visibleConversations(): AgentConversation[] {
    const needle = this.conversationQuery.trim().toLowerCase();
    if (!needle) return this.conversations;
    return this.conversations.filter((c) =>
      (c.title ?? "").toLowerCase().includes(needle),
    );
  }

  get selectedConversation(): AgentConversation | null {
    if (!this.selectedConversationId) return null;
    return (
      this.conversations.find((c) => c.id === this.selectedConversationId) ??
      null
    );
  }

  /**
   * The selected thread's turns, oldest first.
   *
   * ALWAYS filtered by `conversationId`: HQ publishes `ai-agent-messages created`
   * to the whole internal staff channel, so the shared cache legitimately holds
   * other people's turns.
   */
  get messages(): AgentMessage[] {
    if (!this.selectedConversationId) return [];
    const rows = (this.messagesService?.entities ??
      []) as unknown as AgentMessage[];
    return byCreatedAt(
      rows.filter(
        (m) =>
          m.conversationId === this.selectedConversationId &&
          m.role !== "system",
      ),
    );
  }

  /** Tool-audit rows for the runs in the selected thread, keyed by `toolCallId`. */
  private get invocationsByToolCallId(): Map<string, AgentToolInvocation> {
    const runIds = new Set(
      this.messages.map((m) => m.runId).filter((id): id is string => !!id),
    );
    const rows = (this.toolInvocationsService?.entities ??
      []) as unknown as AgentToolInvocation[];
    const map = new Map<string, AgentToolInvocation>();
    for (const row of rows) {
      if (runIds.has(row.runId)) map.set(row.toolCallId, row);
    }
    return map;
  }

  /**
   * The rendered transcript.
   *
   * The persisted shape is one assistant row per LLM turn (text and/or
   * `tool_use` blocks) followed by one `tool`-role row per dispatched call. That
   * is faithful to the loop but noisy to read, so tool calls are folded into a
   * single "tools" item placed where the model asked for them, joined to their
   * `tool_result` block and their `ai_agent_tool_invocations` audit row
   * (status / duration). Nothing is invented: a call with no result yet renders
   * as `running`, never as success.
   */
  get chatItems(): ChatItem[] {
    const items: ChatItem[] = [];
    const invocations = this.invocationsByToolCallId;

    // tool_result blocks, keyed by the call they answer.
    const results = new Map<string, { content?: string; isError?: boolean }>();
    for (const message of this.messages) {
      if (message.role !== "tool") continue;
      for (const block of message.content ?? []) {
        if (block.type === "tool_result" && block.toolCallId) {
          results.set(block.toolCallId, {
            content: block.content,
            isError: block.isError,
          });
        }
      }
    }

    for (const message of this.messages) {
      if (message.role === "tool") continue;

      const text = (message.content ?? [])
        .filter((b) => b.type === "text" && typeof b.text === "string")
        .map((b) => b.text as string)
        .join("\n\n")
        .trim();

      if (message.role === "user") {
        if (text) {
          items.push({
            key: message.id,
            kind: "user",
            text,
            createdAt: message.createdAt,
          });
        }
        continue;
      }

      const toolCalls: ChatToolCall[] = [];
      for (const block of message.content ?? []) {
        if (block.type !== "tool_use" || !block.toolCallId) continue;
        const audit = invocations.get(block.toolCallId);
        const result = results.get(block.toolCallId);
        const status: ChatToolCall["status"] = audit
          ? audit.status === "succeeded"
            ? "succeeded"
            : "failed"
          : result
            ? result.isError
              ? "failed"
              : "succeeded"
            : "running";
        toolCalls.push({
          toolCallId: block.toolCallId,
          name: block.name ?? audit?.toolName ?? "tool",
          input: block.input ?? audit?.input,
          status,
          durationMs: audit?.durationMs ?? null,
          resultPreview: result?.content,
          error: audit?.error ?? null,
        });
      }

      if (text) {
        // The answer's citations, written into the message by the API (never
        // parsed out of the model's words): what it came from, and what could
        // not be read.
        const sources = Array.isArray(message.sources) ? message.sources : [];
        const labels = (kind: "cited" | "unread") =>
          sources
            .filter((s) => (s?.kind === "could-not-read") === (kind === "unread"))
            .map((s) => (typeof s?.label === "string" ? s.label : ""))
            .filter(Boolean);
        const cited = labels("cited");
        const couldNotRead = labels("unread");
        items.push({
          key: message.id,
          kind: "assistant",
          text,
          createdAt: message.createdAt,
          modelId: message.modelId,
          usage: message.usage,
          ...(cited.length ? { cited } : {}),
          ...(couldNotRead.length ? { couldNotRead } : {}),
        });
      }
      if (toolCalls.length > 0) {
        items.push({
          key: `${message.id}:tools`,
          kind: "tools",
          createdAt: message.createdAt,
          toolCalls,
        });
      }
    }

    // The in-flight turn: live tokens, plus any tool the model has asked for
    // that has not produced a persisted `tool_use` block yet.
    if (this.activeRunId) {
      const persisted = this.messages.some(
        (m) => m.runId === this.activeRunId && m.role === "assistant",
      );
      const pendingTools = this.streamToolNames.filter(
        (name) =>
          !items.some(
            (item) =>
              item.kind === "tools" &&
              (item.toolCalls ?? []).some((call) => call.name === name),
          ),
      );
      if (pendingTools.length > 0) {
        items.push({
          key: `${this.activeRunId}:stream-tools`,
          kind: "tools",
          createdAt: new Date().toISOString(),
          pending: true,
          toolCalls: pendingTools.map((name, index) => ({
            toolCallId: `${this.activeRunId}:pending:${index}`,
            name,
            status: "running" as const,
          })),
        });
      }
      if (!persisted) {
        items.push({
          key: `${this.activeRunId}:stream`,
          kind: "assistant",
          text: this.streamText,
          createdAt: new Date().toISOString(),
          pending: true,
        });
      }
    }

    return items;
  }

  get isBusy(): boolean {
    return this.sending || this.activeRunId !== null;
  }

  get canSend(): boolean {
    return (
      !this.isBusy && this.draft.trim().length > 0 && this.agentId !== null
    );
  }

  /** True when nothing about the agent is reachable — the view shows a setup card. */
  get isAgentUnavailable(): boolean {
    return this.agentMissingReason !== null;
  }

  // ── Loading (called from the route hooks) ────────────────────────────────

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.loadFailed = false;
    try {
      // findOrThrow throughout: a bare find SWALLOWS a failure (resolves null).
      // Here that was worst of all — a failed agents read left `agent` null and
      // the page announced that the agent "has not been seeded" and told
      // the admin to run migrations, when the API had merely failed a read.
      // An unregistered agents service keeps its old meaning (no agent → the
      // setup card); only a registered one that fails is a load error.
      if (this.agentsService) {
        await findOrThrow(this.agentsService, {
          query: { name: OFFICE_MANAGER_AGENT_NAME, isActive: true, $limit: 1 },
        });
      }
      const agent = this.agent;
      runInAction(() => {
        this.agentId = agent?.id ?? null;
        this.agentModelId = agent?.defaultModelId ?? null;
        this.agentToolNames = agent?.toolNames ?? [];
        this.agentMissingReason = agent
          ? null
          : `${OFFICE_MANAGER_NAME} has not been set up (no '${OFFICE_MANAGER_AGENT_NAME}' agent row). The API seeds her on every cold start, but the seed is skipped when the @intrasync/ai-agent-api migration ledger has not been applied — run 'yarn migrate' (it chains 'migrate:hq' then 'migrate:packages') on the API and restart it.`;
      });

      if (!this.agentId) return;

      const userId = this.currentUserId;
      await findOrThrow(this.conversationsService, {
        query: {
          agentId: this.agentId,
          status: "active",
          ...(userId === null ? {} : { userId }),
          $sort: { createdAt: -1 },
          $limit: CONVERSATION_PAGE_SIZE,
        },
      });

      const stillThere =
        this.selectedConversationId !== null &&
        this.conversations.some((c) => c.id === this.selectedConversationId);
      if (!stillThere) {
        runInAction(() => {
          this.selectedConversationId = this.conversations[0]?.id ?? null;
        });
      }
      if (this.selectedConversationId) {
        await this.loadMessages(this.selectedConversationId);
      }
      await this.checkAgentChatRoute();
    } catch (err: unknown) {
      // The sign-out ended it: `loadFailed` with no error would mark the
      // conversation list unavailable under an empty error banner.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Failed to load the assistant");
        this.loadFailed = true;
      });
      console.error("Failed to load the AI agent chat:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async reload(): Promise<void> {
    await this.load();
  }

  /** Pull one thread's turns plus the tool-audit rows for its runs. */
  async loadMessages(conversationId: string): Promise<void> {
    this.messagesLoading = true;
    try {
      await findOrThrow(this.messagesService, {
        query: {
          conversationId,
          $sort: { createdAt: 1 },
          $limit: MESSAGE_PAGE_SIZE,
        },
      });
      const runIds = Array.from(
        new Set(
          this.messages.map((m) => m.runId).filter((id): id is string => !!id),
        ),
      );
      // The audit service is optional (older package builds lack it); only a
      // registered one that FAILS is an error.
      if (runIds.length > 0 && this.toolInvocationsService) {
        await findOrThrow(this.toolInvocationsService, {
          query: { runId: { $in: runIds }, $limit: MESSAGE_PAGE_SIZE },
        });
      }
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load the conversation");
      });
      console.error("Failed to load agent messages:", err);
    } finally {
      runInAction(() => {
        this.messagesLoading = false;
      });
    }
  }

  /**
   * Best-effort readiness probe. `llm-task-routes` find is staff-readable, and
   * the API's `resolveAgentLlmBinding` prefers the `agent.chat` route whenever it
   * resolves to an ENABLED azureOpenAI provider — so an unrouted or wrong-vendor
   * route is the one misconfiguration the browser can actually see. The config
   * fallback (`azure.openai.endpoint` / `.key`) is server-only and invisible
   * here, which is why the warning says so instead of claiming a hard failure.
   */
  async checkAgentChatRoute(): Promise<void> {
    const routes = this.serviceStore("llm-task-routes");
    if (!routes) return;
    try {
      // findOrThrow so a failed read lands in the tolerant catch below. A
      // swallowed failure read as zero routes and raised a FALSE "agent.chat
      // is not routed" warning. Still best-effort: no warning either way.
      await findOrThrow(routes, { query: {} });
      const rows = (routes.entities ?? []) as unknown as AgentChatRouteRow[];
      const row = rows.find((r) => r.taskKey === AGENT_CHAT_TASK_KEY);
      runInAction(() => {
        if (!row || !row.providerId) {
          this.configWarning = `The '${AGENT_CHAT_TASK_KEY}' task is not routed to a provider, so the API falls back to its server-side azure.openai config block (azure.openai.endpoint + azure.openai.key, i.e. AZURE_OPENAI_ENDPOINT / AZURE_OPENAI_KEY). If that is unset, every run fails with "no Azure OpenAI target for the agent". Route it under AI → Task Routing.`;
        } else if (!AGENT_CAPABLE_PROVIDER_TYPES.includes(String(row.providerType))) {
          // Mirrors AGENT_CAPABLE_PROVIDERS on the API. This used to warn on
          // anything but Azure — after the Anthropic, Gemini and OpenAI
          // adapters landed, that told an admin to undo a route that worked.
          this.configWarning = `The '${AGENT_CHAT_TASK_KEY}' task is routed to a '${row.providerType}' provider (${row.providerName ?? "unnamed"}). The agent loop needs tool-calling + streaming, which HQ implements for Azure OpenAI, OpenAI, Anthropic and Gemini — not this type — so the API refuses the route and falls back to its azure.openai config block. Re-route it to one of those.`;
        } else {
          this.configWarning = null;
        }
      });
    } catch (err: unknown) {
      // Never fail the page over a readiness hint.
      console.warn("ai-agent: could not read the agent.chat task route", err);
    }
  }

  // ── View state ───────────────────────────────────────────────────────────

  setDraft(value: string): void {
    this.draft = value;
    if (this.sendError) this.sendError = null;
  }

  setConversationQuery(value: string): void {
    this.conversationQuery = value;
  }

  toggleConversationList(open?: boolean): void {
    this.conversationListOpen = open ?? !this.conversationListOpen;
  }

  async openConversation(conversationId: string): Promise<void> {
    if (this.selectedConversationId === conversationId) {
      this.conversationListOpen = false;
      return;
    }
    this.selectedConversationId = conversationId;
    this.conversationListOpen = false;
    this.sendError = null;
    await this.loadMessages(conversationId);
  }

  startNewConversation(): void {
    this.selectedConversationId = null;
    this.conversationListOpen = false;
    this.sendError = null;
    this.streamText = "";
    this.streamToolNames = [];
  }

  /** Conversations are never deleted — archiving is `status: 'archived'`. */
  async archiveConversation(conversationId: string): Promise<void> {
    // A sign-out during the patch: the thread to fall back to, and its
    // messages, would be picked from (and read in) the next person's session.
    const ended = sessionMark(this.rootStore);
    try {
      // THROW_ON_ERROR: the ServiceStore otherwise RESOLVES a refusal, and a
      // sign-out, as null — and the archive read as done.
      await this.conversationsService?.patch(
        conversationId,
        { status: "archived" } as never,
        THROW_ON_ERROR,
      );
      if (ended()) return;
      runInAction(() => {
        if (this.selectedConversationId === conversationId) {
          this.selectedConversationId = this.conversations[0]?.id ?? null;
        }
      });
      if (this.selectedConversationId) {
        await this.loadMessages(this.selectedConversationId);
      }
    } catch (err: unknown) {
      console.error("Failed to archive an agent conversation:", err);
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.error =
          err instanceof Error
            ? err.message
            : "Failed to archive the conversation";
      });
    }
  }

  // ── The turn ─────────────────────────────────────────────────────────────

  /**
   * One user turn: ensure a conversation, append the user message, create a run,
   * then `start` it and await the whole agent loop.
   *
   * This is exactly the sequence the package's own `fireAgentRun` helper uses
   * (dist/shared/automation/fire-agent-run.js) — conversation → user message →
   * run → start — so the web client and the server-side automations drive the
   * platform identically. `companyId` is omitted on purpose: HQ's tenancy hook
   * stamps it on every external create (and runs BEFORE validation, because
   * Feathers collects `before.all` ahead of method hooks).
   */
  async send(): Promise<boolean> {
    const text = this.draft.trim();
    if (!text || !this.agentId || this.isBusy) return false;

    const actions = this.runActions;
    if (!actions) {
      runInAction(() => {
        this.sendError =
          "The realtime connection is not available, so the run could not be started.";
      });
      return false;
    }

    this.sending = true;
    this.sendError = null;
    this.streamText = "";
    this.streamToolNames = [];

    let runId: string | null = null;
    // A sign-out while the run is out ends the send: no failure to explain, no
    // run to re-read and no messages to refresh — each of those would be a
    // call started in the NEXT session about the previous person's run.
    const ended = sessionMark(this.rootStore);
    try {
      let conversationId = this.selectedConversationId;
      if (!conversationId) {
        const created = (await this.conversationsService?.create({
          agentId: this.agentId,
          userId: this.currentUserId,
          title: deriveConversationTitle(text),
          status: "active",
        } as never)) as unknown as AgentConversation | undefined;
        if (ended()) return false;
        if (!created?.id)
          throw new Error("The conversation could not be created.");
        conversationId = created.id;
        runInAction(() => {
          this.selectedConversationId = created.id;
        });
      }

      const posted = await this.messagesService?.create({
        conversationId,
        role: "user",
        content: [{ type: "text", text }],
        status: "complete",
      } as never);
      if (ended()) return false;
      // The ServiceStore RESOLVES a refused create (and a sign-out) as null:
      // the turn was not saved, so a run over it would answer nothing.
      if (!posted) {
        throw swallowedFailure(this.messagesService, "errorOnCreate", "Your message could not be saved.");
      }

      runInAction(() => {
        this.draft = "";
      });

      const run = (await this.runsService?.create({
        conversationId,
        agentId: this.agentId,
      } as never)) as unknown as AgentRun | undefined;
      if (ended()) return false;
      if (!run?.id) throw new Error("The run could not be created.");
      runId = run.id;
      runInAction(() => {
        this.activeRunId = run.id;
      });

      // Long-lived: resolves only when the whole loop settles. HQ's socket has
      // no ack timeout, which is what makes this safe.
      await actions.start(run.id);
      return !ended();
    } catch (err: unknown) {
      if (isSessionEnded(err) || ended()) return false;
      let run: AgentRun | null = null;
      if (runId) {
        try {
          run = (await this.runsService?.get(runId)) as unknown as AgentRun;
        } catch {
          run = null;
        }
      }
      const message = describeRunFailure(err, run);
      runInAction(() => {
        if (!ended()) this.sendError = message;
      });
      console.error("Agent run failed:", err);
      return false;
    } finally {
      const conversationId = this.selectedConversationId;
      // The persisted messages are the source of truth; re-read them (and the
      // tool audit) now that the run has settled, then drop the stream buffer.
      if (conversationId && !ended()) {
        await this.loadMessages(conversationId);
        try {
          await this.conversationsService?.find({
            query: { id: conversationId, $limit: 1 },
          });
        } catch {
          /* projection refresh is cosmetic (messageCount / lastMessageAt) */
        }
      }
      // After a sign-out the reset has cleared these already, and since then
      // they may be the next person's own send in progress.
      if (!ended()) {
        runInAction(() => {
          this.sending = false;
          this.activeRunId = null;
          this.streamText = "";
          this.streamToolNames = [];
        });
      }
    }
  }

  /** Ask the API to cancel the in-flight run. Terminal runs are a server no-op. */
  async cancelRun(): Promise<void> {
    const runId = this.activeRunId;
    const actions = this.runActions;
    if (!runId || !actions) return;
    try {
      await actions.cancel(runId);
    } catch (err: unknown) {
      console.warn("Failed to cancel the agent run:", err);
    }
  }

  // ── Run streaming ────────────────────────────────────────────────────────

  /**
   * Subscribe to the transient `run.stream.*` events on `ai-agent-runs`.
   *
   * These are declared in the service's `events` list server-side, so the socket
   * transport publishes them; `channels.ts` routes them to `user/<createdBy>`
   * only. Client-side a socket service's `on` namespaces the event onto the
   * shared connection, so no service registration is needed to receive them.
   */
  attachRunStream(): void {
    if (this.streamHandlers.size > 0) return;
    const service = (
      this.rootStore?.app as unknown as {
        service?: (path: string) => EventfulService;
      }
    )?.service?.(AI_AGENT_RUNS_PATH);
    if (!service?.on) return;

    for (const event of RUN_STREAM_EVENTS) {
      const handler = ((payload: RunStreamEvent) => {
        this.handleRunStream(event, payload);
      }) as (payload: never) => void;
      this.streamHandlers.set(event, handler);
      service.on(event, handler);
    }
  }

  detachRunStream(): void {
    const service = (
      this.rootStore?.app as unknown as {
        service?: (path: string) => EventfulService;
      }
    )?.service?.(AI_AGENT_RUNS_PATH);
    for (const [event, handler] of this.streamHandlers) {
      const off = service?.off ?? service?.removeListener;
      off?.call(service, event, handler);
    }
    this.streamHandlers.clear();
  }

  /** Fold one stream event into the transient buffer. Ignores other runs. */
  handleRunStream(event: string, payload: RunStreamEvent): void {
    if (!payload || payload.runId !== this.activeRunId) return;
    runInAction(() => {
      if (event === "run.stream.start") {
        this.streamText = "";
      } else if (event === "run.stream.chunk") {
        this.streamText += payload.chunk ?? "";
      } else if (event === "run.stream.tool") {
        const name = payload.toolName;
        if (name && !this.streamToolNames.includes(name)) {
          this.streamToolNames.push(name);
        }
        // A tool turn means the visible answer restarts on the next round.
        this.streamText = "";
      }
      // `run.stream.end` closes one LLM turn but the loop may continue; the
      // buffer is cleared by the persisted message landing, not by `end`.
    });
  }

  dispose(): void {
    this.detachRunStream();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.loading = false;
    this.error = null;
    this.loadFailed = false;
    this.agentId = null;
    this.agentModelId = null;
    this.agentToolNames = [];
    this.agentMissingReason = null;
    this.selectedConversationId = null;
    this.messagesLoading = false;
    this.conversationQuery = "";
    this.draft = "";
    this.sending = false;
    this.sendError = null;
    this.activeRunId = null;
    this.streamText = "";
    this.streamToolNames = [];
    this.configWarning = null;
    this.conversationListOpen = false;
  }
}

/**
 * Lazily-mounted singleton per RootStore.
 *
 * The module owns no entry in `appStoresConfig.ts` on purpose: that file is the
 * shared composition root and this feature is meant to be addable by touching
 * only `routesList.tsx` + `serviceStoresConfig.ts`. Keyed weakly so a torn-down
 * RootStore (tests, hot reload) is not pinned in memory.
 */
const instances = new WeakMap<object, AgentChatStore>();

export const getAgentChatStore = (rootStore: RootStore): AgentChatStore => {
  const key = rootStore as unknown as object;
  let store = instances.get(key);
  if (!store) {
    store = new AgentChatStore(rootStore);
    instances.set(key, store);
    // Not an app store, so purgeUserData would never reach it: an unsent draft,
    // a streamed answer and the open thread would outlive the sign-out.
    rootStore.registerSessionStore("agentChatStore", store);
  }
  return store;
};

export default AgentChatStore;
